import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../data-model/medicalPaOrdersModel";
import { MedicalPaOrdersAuthStatus } from "../../../enums/medicalPaOrdersAuthStatus";
import {
  useCandidUpdateEnabled,
  useFacilityCategory,
  useOncoemrTasksEnabled,
} from "../../../hooks/useOncoemrTasksEnabled";
import { RootState } from "../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { capitalizeWordsSeperatedByUnderScore } from "../../../utils/stringModifications";
import {
  getCurrentStatusBadge,
  getTabSwitchElements,
  shouldOpenHappyModal,
} from "../utils/orderDataHelpers";
import { saveJCodeDocsToLocalStorage } from "../utils/saveJCodeDocstoLS";

/**
 * Derives UI-facing computed values for the single-order layout from the
 * Postgres-backed data-accumulator context: current status badge, tab
 * list (including facility-gated Tasks / Candid Update tabs), "Mark as
 * completed" / "Shift to queue" button text, matched submission form
 * configs, and the happy-flow modal gate.
 *
 * @returns Memoised values consumed by the layout footer and modals
 */
export const useOrderComputedValues = () => {
  const { generalOrderDetails, authOnFileDetails, workflowConfig } =
    useDataAccumulatorForSingleOrder();
  const orderId = generalOrderDetails?.order?.order_id;
  const { formConfigs } = useSelector(
    (state: RootState) => state.formConfigNoLiveListen,
  );
  const orgId = getOrgIdForFetchExternalWorklist();
  const facilityCategory = useFacilityCategory();
  const isOncoemrTasksEnabled = useOncoemrTasksEnabled();
  const isCandidUpdateEnabled = useCandidUpdateEnabled();

  const currentStatusBadge = useMemo(() => {
    return getCurrentStatusBadge(generalOrderDetails?.order);
  }, [generalOrderDetails?.order]);

  const tabSwitchElements = useMemo(
    () =>
      getTabSwitchElements(
        orgId,
        isOncoemrTasksEnabled,
        isCandidUpdateEnabled,
        facilityCategory,
      ),
    [orgId, isOncoemrTasksEnabled, isCandidUpdateEnabled, facilityCategory],
  );

  const buttonTextforMarkAsCompleted = useMemo(() => {
    const shiftMasterQueueStatus = [
      MedicalPaOrdersAuthStatus.Pending,
      MedicalPaOrdersAuthStatus.AuthRequired,
      MedicalPaOrdersAuthStatus.Query,
      MedicalPaOrdersAuthStatus.Hold,
    ];

    const masterAuthStatus =
      generalOrderDetails?.order?.status?.master_auth_status;
    const authOnFileStatus = authOnFileDetails?.auth_on_file?.auth_status;

    if (masterAuthStatus === authOnFileStatus) {
      return "Mark as completed";
    }

    if (
      shiftMasterQueueStatus.includes(
        authOnFileDetails?.auth_on_file
          ?.auth_status as MedicalPaOrdersAuthStatus,
      )
    ) {
      const authStatusLabel = capitalizeWordsSeperatedByUnderScore(
        authOnFileDetails?.auth_on_file
          ?.auth_status as MedicalPaOrdersAuthStatus,
      );
      return `Shift to ${authStatusLabel} Queue`;
    }
    return "Mark as completed";
  }, [authOnFileDetails?.auth_on_file?.auth_status]);

  const allMatchedFormConfigs = useMemo(() => {
    if (
      formConfigs &&
      workflowConfig &&
      workflowConfig?.submission_form_config_id !== ""
    ) {
      const allSubmissionFormConfigs =
        workflowConfig?.submission_form_config_id?.split(",");
      const allFormConfigs = formConfigs.filter((formConfig) =>
        allSubmissionFormConfigs?.includes(formConfig.id),
      );

      const dataToShow = allFormConfigs.map((formConfig) => {
        return {
          id: formConfig?.id,
          name: formConfig?.data?.formTitle ?? "",
        };
      });
      return dataToShow;
    }
    return [];
  }, [formConfigs, workflowConfig, facilityCategory]);

  const shouldOpenHappyModalValue = useMemo(() => {
    return shouldOpenHappyModal(
      generalOrderDetails?.order,
      orderId,
      authOnFileDetails?.auth_on_file,
    );
  }, [generalOrderDetails?.order, orderId, authOnFileDetails?.auth_on_file]);

  useEffect(() => {
    saveJCodeDocsToLocalStorage(
      generalOrderDetails?.order as GeneralOrderDetails,
      authOnFileDetails?.auth_on_file as AuthOnFile,
    );
  }, [generalOrderDetails?.order, authOnFileDetails?.auth_on_file]);

  return {
    currentStatusBadge,
    tabSwitchElements,
    buttonTextforMarkAsCompleted,
    allMatchedFormConfigs,
    shouldOpenHappyModalValue,
  };
};
